import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { toast } from '@/hooks/use-toast';
import { useTenant } from '@/hooks/useTenant';
import type { TrackingHours } from '@/utils/fleetStatus';
import { FLEET_KEY } from './useFleetLive';

export interface LocationTrackingSettings {
  location_tracking_enabled: boolean;
  location_notice_confirmed_at: string | null;
  location_notice_confirmed_by: string | null;
  location_tracking_hours: TrackingHours;
  location_history_days: number;
  business_hours: string | null;
}

/**
 * The company's technician-tracking settings (migration 20261019130000). Read on their own rather
 * than through COMPANY_SELECT_COLUMNS, so the rest of the app never depends on these columns.
 * Keyed under ['tenant'] so the companies broadcast refreshes it.
 */
export function useLocationTrackingSettings() {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: ['tenant', 'location-tracking', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('location_tracking_enabled, location_notice_confirmed_at, location_notice_confirmed_by, location_tracking_hours, location_history_days, business_hours')
        .eq('id', tenantId as string)
        .single();
      if (error) throw error;
      return data as unknown as LocationTrackingSettings;
    },
  });
}

export interface SaveLocationTrackingInput {
  enabled: boolean;
  hours: TrackingHours;
  noticeConfirmed: boolean;
}

/** set_location_tracking_settings: admin only; turning on needs the written-notice attestation. */
export function useSaveLocationTracking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ enabled, hours, noticeConfirmed }: SaveLocationTrackingInput) => {
      const { error } = await supabase.rpc('set_location_tracking_settings', {
        p_enabled: enabled,
        p_hours: hours as Json,
        p_notice_confirmed: noticeConfirmed,
      });
      if (error) throw new Error(error.message.replace(/^(NOTICE_REQUIRED|INVALID|FORBIDDEN):\s*/, ''));
    },
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({ queryKey: ['tenant', 'location-tracking'] });
      queryClient.invalidateQueries({ queryKey: FLEET_KEY });
      toast({
        title: input.enabled ? 'Technician tracking is on' : 'Technician tracking saved',
        description: input.enabled
          ? 'Technicians share their location in the Steddy app during the hours you set, once they accept the notice.'
          : 'No locations are recorded while tracking is off.',
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not save tracking settings', description: error.message, variant: 'destructive' });
    },
  });
}

/** Exempt one technician (on leave, office-only) or include them again. */
export function useSetTechTracking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ techId, enabled }: { techId: string; enabled: boolean; name: string }) => {
      const { error } = await supabase
        .from('techs')
        .update({ location_tracking_enabled: enabled })
        .eq('id', techId);
      if (error) throw error;
    },
    onSuccess: (_, { enabled, name }) => {
      queryClient.invalidateQueries({ queryKey: FLEET_KEY });
      queryClient.invalidateQueries({ queryKey: ['techs'] });
      toast({ title: enabled ? `${name} is tracked again` : `${name} is no longer tracked` });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not change tracking', description: error.message, variant: 'destructive' });
    },
  });
}
